import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, Clock, Phone, User, PlusCircle, Filter, X, Upload, CheckCircle, Truck, CreditCard, Box, AlertCircle, Trash2, ArrowRight } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { useAppContext } from '../../hooks/useAppContext';
import { differenceInHours, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';
import { FileUploadModal } from '../../components/common/FileUploadModal';
import { ConfirmationModal } from '../../components/common/ConfirmationModal';

type PackageStatus = 'NEW' | 'PAYMENT_CONFIRMED' | 'PROCESSING' | 'READY_FOR_DELIVERY' | 'DELIVERED' | 'CANCELLED';
interface PackageRequest {
    id: string;
    title: string;
    description: string;
    customerName: string;
    customerPhone: string;
    priority: 'low' | 'medium' | 'high';
    status: PackageStatus;
    progressPercent: number;
    creationDate: string;
    lastModified: string;
    employeeId: string;
    employeeName?: string;
    branch: string;
    meta?: Record<string, any>;
}

const statusBadge = (status: PackageStatus) => {
    switch (status) {
        case 'NEW': return <span className="px-2 py-1 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 text-xs font-medium">جديد</span>;
        case 'PAYMENT_CONFIRMED': return <span className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-medium">تم تأكيد الدفع</span>;
        case 'PROCESSING': return <span className="px-2 py-1 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-medium">قيد المعالجة</span>;
        case 'READY_FOR_DELIVERY': return <span className="px-2 py-1 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs font-medium">جاهز للتسليم</span>;
        case 'DELIVERED': return <span className="px-2 py-1 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 text-xs font-medium">تم التسليم</span>;
        case 'CANCELLED': return <span className="px-2 py-1 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-medium">ملغي</span>;
        default: return <span className="px-2 py-1 rounded-lg bg-slate-500/20 text-slate-400 border border-slate-500/30 text-xs font-medium">غير معروف</span>;
    }
};

const timeAgo = (isoDate: string) => {
    const date = new Date(isoDate);
    const hours = differenceInHours(new Date(), date);
    if (hours < 24) return `${hours} ساعة مضت`;
    const days = differenceInDays(new Date(), date);
    return `${days} يوم مضى`;
};

const ModernPackageRequestsScreen: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<PackageStatus | 'ALL'>('ALL');
    const [packages, setPackages] = useState<PackageRequest[]>([]);
    const [offline, setOffline] = useState(false);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [packageType, setPackageType] = useState('');
    const [deliveryMethod, setDeliveryMethod] = useState('');
    const [modifications, setModifications] = useState('');
    const [customerLocation, setCustomerLocation] = useState('');
    const [isPaid, setIsPaid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [actionComment, setActionComment] = useState<Record<string, string>>({});
    const [readyFiles, setReadyFiles] = useState<Record<string, File[]>>({});
    const [paymentFiles, setPaymentFiles] = useState<Record<string, File[]>>({});
    const [isUploadOpen, setUploadOpen] = useState(false);
    const [uploadMode, setUploadMode] = useState<'payment' | 'shipping' | null>(null);
    const [selectedReqId, setSelectedReqId] = useState<string | null>(null);
    const [isDeleteOpen, setDeleteOpen] = useState(false);

    const openUpload = (id: string, mode: 'payment' | 'shipping') => {
        setSelectedReqId(id);
        setUploadMode(mode);
        setUploadOpen(true);
    };

    const closeUpload = () => {
        setUploadOpen(false);
        setSelectedReqId(null);
        setUploadMode(null);
    };

    const handleUploadSubmit = async (files: File[]) => {
        if (!selectedReqId || !uploadMode) return;
        const comment = actionComment[selectedReqId] || '';
        if (uploadMode === 'payment') {
            await confirmPayment(selectedReqId, files, comment);
        } else {
            await markReady(selectedReqId, files, comment);
        }
        closeUpload();
    };

    useEffect(() => {
        const fetchPackages = async () => {
            if (!user) return;
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE_URL}/package-requests`, {
                    headers: { 'X-User-Id': user.id, 'X-User-Role': user.role }
                });

                if (!res.ok) {
                    throw new Error(`خطأ في استجابة الخادم: ${res.status}`);
                }

                let data;
                try {
                    data = await res.json();
                } catch (jsonError) {
                    console.error('Failed to parse JSON response', jsonError);
                    throw new Error('تعذر قراءة البيانات من الخادم');
                }

                setPackages(Array.isArray(data) ? data : []);
                setOffline(false);
            } catch (err) {
                console.error('Failed to load package requests', err);
                setOffline(true);
                setPackages([]);
                toast.error('تعذر الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى لاحقاً.');
            } finally {
                setLoading(false);
            }
        };
        fetchPackages();
    }, [user]);

    const handleCreate = async () => {
        if (!user) { toast.error('الرجاء تسجيل الدخول'); return; }
        if (!customerName.trim() || !customerPhone.trim() || !packageType.trim() || !deliveryMethod.trim()) {
            toast.error('الرجاء إدخال البيانات المطلوبة');
            return;
        }
        try {
            setIsSubmitting(true);
            const res = await fetch(`${API_BASE_URL}/package-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    employeeId: user.employeeId,
                    title: `${packageType} - ${customerName}`,
                    description: modifications.trim() || 'لا توجد ملاحظات',
                    customerName: customerName.trim(),
                    customerPhone: customerPhone.trim(),
                    packageType: packageType.trim(),
                    deliveryMethod: deliveryMethod.trim(),
                    modifications: modifications.trim(),
                    customerLocation: customerLocation.trim(),
                    isPaid,
                    priority: 'medium',
                    meta: { source: 'dashboard', packageType, deliveryMethod, isPaid, customerLocation }
                })
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({ message: 'فشل إنشاء الطلب' }));
                throw new Error(errData.message || 'فشل إنشاء الطلب');
            }
            const newReq: PackageRequest = await res.json();
            setPackages(prev => [newReq, ...prev]);
            toast.success('تم إنشاء طلب جديد بنجاح');
            setIsCreateOpen(false);
            setCustomerName(''); setCustomerPhone(''); setPackageType(''); setDeliveryMethod(''); setModifications(''); setCustomerLocation(''); setIsPaid(false);
        } catch (e: any) {
            console.error('Create package error', e);
            toast.error(e.message || 'حدث خطأ أثناء إنشاء الطلب');
        } finally {
            setIsSubmitting(false);
        }
    };

    const filtered = useMemo(() => {
        if (!Array.isArray(packages)) return [];

        return packages.filter(p => {
            const matchesQuery = [p.title, p.customerName, p.employeeName, p.branch].some(v => String(v || '').toLowerCase().includes(query.toLowerCase()));
            const matchesStatus = statusFilter === 'ALL' ? true : p.status === statusFilter;
            return matchesQuery && matchesStatus;
        });
    }, [packages, query, statusFilter]);

    const updateRequestInList = (updated: PackageRequest) => {
        setPackages(prev => prev.map(p => (p.id === updated.id ? updated : p)));
    };

    const confirmPayment = async (id: string, overrideFiles?: File[], overrideComment?: string) => {
        if (!user) { toast.error('الرجاء تسجيل الدخول'); return; }
        if (offline) { toast.error('الخادم غير متصل، لا يمكن تأكيد الدفع حالياً'); return; }
        const files = overrideFiles ?? (paymentFiles[id] || []);
        if (files.length === 0) { toast.error('يجب إرفاق سند الدفع'); return; }
        try {
            const form = new FormData();
            form.append('employeeId', user.employeeId);
            form.append('comment', (overrideComment ?? actionComment[id]) || '');
            files.forEach((file) => form.append('payment_proof', file));
            const res = await fetch(`${API_BASE_URL}/package-requests/${id}/confirm-payment`, {
                method: 'POST',
                body: form,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: 'فشل تأكيد الدفع' }));
                throw new Error(err.message || 'فشل تأكيد الدفع');
            }
            const updated: PackageRequest = await res.json();
            updateRequestInList(updated);
            toast.success('تم تأكيد الدفع وإرفاق سند الدفع');
            setPaymentFiles(prev => ({ ...prev, [id]: [] }));
        } catch (e: any) {
            toast.error(e.message || 'حدث خطأ أثناء تأكيد الدفع');
        }
    };

    const startProcessing = async (id: string) => {
        if (!user) { toast.error('الرجاء تسجيل الدخول'); return; }
        if (offline) { toast.error('الخادم غير متصل، لا يمكن بدء التنفيذ حالياً'); return; }
        try {
            const res = await fetch(`${API_BASE_URL}/package-requests/${id}/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ employeeId: user.employeeId, comment: actionComment[id] || '' })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: 'فشل بدء التنفيذ' }));
                throw new Error(err.message || 'فشل بدء التنفيذ');
            }
            const updated: PackageRequest = await res.json();
            updateRequestInList(updated);
            toast.success('تم بدء التنفيذ');
        } catch (e: any) {
            toast.error(e.message || 'حدث خطأ أثناء بدء التنفيذ');
        }
    };

    const markReady = async (id: string, overrideFiles?: File[], overrideComment?: string) => {
        if (!user) { toast.error('الرجاء تسجيل الدخول'); return; }
        if (offline) { toast.error('الخادم غير متصل، لا يمكن تجهيز الطلب حالياً'); return; }
        const files = overrideFiles ?? (readyFiles[id] || []);
        if (files.length === 0) { toast.error('يرجى إرفاق بوليصة الشحن'); return; }
        try {
            const form = new FormData();
            form.append('employeeId', user.employeeId);
            form.append('comment', (overrideComment ?? actionComment[id]) || '');
            files.forEach((file) => form.append('shipping_docs', file));
            const res = await fetch(`${API_BASE_URL}/package-requests/${id}/mark-ready`, {
                method: 'POST',
                body: form,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: 'فشل تجهيز الطلب' }));
                throw new Error(err.message || 'فشل تجهيز الطلب');
            }
            const updated: PackageRequest = await res.json();
            updateRequestInList(updated);
            toast.success('تم التجهيز وإرفاق بوليصة الشحن');
            setReadyFiles(prev => ({ ...prev, [id]: [] }));
        } catch (e: any) {
            toast.error(e.message || 'حدث خطأ أثناء التجهيز');
        }
    };

    const confirmDelivery = async (id: string) => {
        if (!user) { toast.error('الرجاء تسجيل الدخول'); return; }
        if (offline) { toast.error('الخادم غير متصل، لا يمكن تأكيد الاستلام حالياً'); return; }
        try {
            const res = await fetch(`${API_BASE_URL}/package-requests/${id}/confirm-delivery`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ employeeId: user.employeeId, comment: actionComment[id] || '' })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: 'فشل تأكيد الاستلام' }));
                throw new Error(err.message || 'فشل تأكيد الاستلام');
            }
            const updated: PackageRequest = await res.json();
            updateRequestInList(updated);
            toast.success('تم تأكيد الاستلام');
        } catch (e: any) {
            toast.error(e.message || 'حدث خطأ أثناء تأكيد الاستلام');
        }
    };

    const deleteRequest = async (id: string) => {
        if (!user) { toast.error('الرجاء تسجيل الدخول'); return; }
        if (offline) { toast.error('الخادم غير متصل، لا يمكن حذف الطلب حالياً'); return; }
        try {
            const res = await fetch(`${API_BASE_URL}/package-requests/${id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ employeeId: user.employeeId })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: 'فشل حذف الطلب' }));
                throw new Error(err.message || 'فشل حذف الطلب');
            }
            setPackages(prev => prev.filter(p => p.id !== id));
            toast.success('تم حذف الطلب');
        } catch (e: any) {
            toast.error(e.message || 'حدث خطأ أثناء حذف الطلب');
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">إدارة البكجات</h1>
                    <p className="text-slate-400">متابعة وإدارة طلبات البكجات والشحن</p>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                        <input
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="بحث..."
                            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 pr-10 pl-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                    <button
                        onClick={() => setIsCreateOpen(true)}
                        className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2"
                    >
                        <PlusCircle size={18} />
                        <span className="hidden md:inline">طلب جديد</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {(['ALL', 'NEW', 'PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED', 'CANCELLED'] as const).map(s => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s as any)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${statusFilter === s
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
                            }`}
                    >
                        {s === 'ALL' ? 'الكل' : statusBadge(s as PackageStatus)}
                    </button>
                ))}
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                    <Package size={64} className="mb-4 opacity-20" />
                    <p>لا توجد طلبات حالياً</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map(req => (
                        <div
                            key={req.id}
                            className="glass-card p-5 rounded-2xl hover:bg-slate-800/50 transition-all cursor-pointer group relative overflow-hidden"
                            onClick={() => navigate(`/packages/${req.id}`, { state: { initial: req } })}
                        >
                            <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                            <div className="flex justify-between items-start mb-4">
                                <h3 className="font-bold text-white truncate max-w-[70%]">{req.title || 'طلب بدون عنوان'}</h3>
                                {statusBadge(req.status)}
                            </div>

                            <div className="space-y-3 text-sm text-slate-400 mb-4">
                                <div className="flex items-center gap-2">
                                    <User size={14} className="text-blue-400" />
                                    <span className="truncate">{req.employeeName || req.employeeId}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${['PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED'].includes(req.status) ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                                    <span className={['PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED'].includes(req.status) ? 'text-emerald-400' : 'text-red-400'}>
                                        {['PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED'].includes(req.status) ? 'مدفوع' : 'غير مدفوع'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone size={14} className="text-purple-400" />
                                    <span className="truncate">{req.customerName}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock size={14} className="text-amber-400" />
                                    <span className="truncate">{timeAgo(req.creationDate)}</span>
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="flex justify-between items-center mb-1 text-xs text-slate-500">
                                    <span>التقدم</span>
                                    <span className="font-semibold text-blue-400">{req.progressPercent}%</span>
                                </div>
                                <div className="w-full bg-slate-700/50 rounded-full h-1.5">
                                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${req.progressPercent}%` }}></div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-white/5 space-y-3" onClick={e => e.stopPropagation()}>
                                <input
                                    value={actionComment[req.id] || ''}
                                    onChange={(e) => setActionComment(prev => ({ ...prev, [req.id]: e.target.value }))}
                                    placeholder="ملاحظة للإجراء..."
                                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg py-2 px-3 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                                />

                                <div className="flex flex-wrap gap-2">
                                    {req.status === 'NEW' && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); openUpload(req.id, 'payment'); }}
                                            className="flex-1 py-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 text-xs font-medium transition-colors"
                                        >
                                            تأكيد الدفع
                                        </button>
                                    )}

                                    {req.status === 'PAYMENT_CONFIRMED' && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); startProcessing(req.id); }}
                                            className="flex-1 py-2 rounded-lg bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 text-xs font-medium transition-colors"
                                        >
                                            بدء التنفيذ
                                        </button>
                                    )}

                                    {req.status === 'PROCESSING' && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); openUpload(req.id, 'shipping'); }}
                                            className="flex-1 py-2 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-xs font-medium transition-colors"
                                        >
                                            تم التجهيز
                                        </button>
                                    )}

                                    {req.status === 'READY_FOR_DELIVERY' && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); confirmDelivery(req.id); }}
                                            className="flex-1 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-medium transition-colors"
                                        >
                                            تأكيد الاستلام
                                        </button>
                                    )}

                                    <button
                                        onClick={(e) => { e.stopPropagation(); setSelectedReqId(req.id); setDeleteOpen(true); }}
                                        className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            {isCreateOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="w-full max-w-2xl bg-[#0f172a] border border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-white">إنشاء طلب جديد</h3>
                            <button onClick={() => setIsCreateOpen(false)} className="text-slate-400 hover:text-white transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-2">اسم العميل *</label>
                                    <input
                                        value={customerName}
                                        onChange={e => setCustomerName(e.target.value)}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                        placeholder="الاسم الكامل"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-2">رقم الجوال *</label>
                                    <input
                                        value={customerPhone}
                                        onChange={e => setCustomerPhone(e.target.value)}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                        placeholder="05xxxxxxxx"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-2">نوع البكج *</label>
                                    <input
                                        value={packageType}
                                        onChange={e => setPackageType(e.target.value)}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                        placeholder="مثال: تركيب ألواح شمسية"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-2">طريقة الاستلام *</label>
                                    <select
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                                        value={deliveryMethod}
                                        onChange={e => setDeliveryMethod(e.target.value)}
                                    >
                                        <option value="">اختر طريقة الاستلام</option>
                                        <option value="استلام من الفرع">استلام من الفرع</option>
                                        <option value="توصيل للموقع">توصيل للموقع</option>
                                        <option value="شحن">شحن</option>
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-slate-400 mb-2">موقع العميل</label>
                                    <input
                                        value={customerLocation}
                                        onChange={e => setCustomerLocation(e.target.value)}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                        placeholder="مثال: الرياض - حي اليرموك"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-slate-400 mb-2">التعديلات والملاحظات</label>
                                    <textarea
                                        value={modifications}
                                        onChange={e => setModifications(e.target.value)}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 min-h-[100px]"
                                        placeholder="أي تعديلات أو ملاحظات خاصة بالطلب..."
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-slate-400 mb-2">حالة الدفع</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isPaid ? 'border-emerald-500 bg-emerald-500' : 'border-slate-600'}`}>
                                                {isPaid && <CheckCircle size={14} className="text-white" />}
                                            </div>
                                            <input type="radio" name="payment" checked={isPaid} onChange={() => setIsPaid(true)} className="hidden" />
                                            <span className={isPaid ? 'text-white' : 'text-slate-400'}>مدفوع</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${!isPaid ? 'border-red-500 bg-red-500' : 'border-slate-600'}`}>
                                                {!isPaid && <CheckCircle size={14} className="text-white" />}
                                            </div>
                                            <input type="radio" name="payment" checked={!isPaid} onChange={() => setIsPaid(false)} className="hidden" />
                                            <span className={!isPaid ? 'text-white' : 'text-slate-400'}>غير مدفوع</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-700 flex justify-end gap-3 bg-slate-900/50">
                            <button
                                onClick={() => setIsCreateOpen(false)}
                                className="px-6 py-2.5 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                            >
                                إلغاء
                            </button>
                            <button
                                onClick={handleCreate}
                                disabled={isSubmitting}
                                className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                {isSubmitting && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                                <span>{isSubmitting ? 'جاري الإنشاء...' : 'إنشاء الطلب'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <FileUploadModal
                isOpen={isUploadOpen}
                onClose={closeUpload}
                onSubmit={handleUploadSubmit}
                title={uploadMode === 'payment' ? 'إرفاق سند الدفع' : 'إرفاق بوليصة الشحن'}
                submitButtonText={uploadMode === 'payment' ? 'تأكيد الدفع' : 'تم التجهيز'}
                required={true}
            />

            <ConfirmationModal
                isOpen={isDeleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={async () => { if (!selectedReqId) return; await deleteRequest(selectedReqId); setDeleteOpen(false); }}
                title="تأكيد حذف الطلب"
                message="هل أنت متأكد من حذف الطلب؟ لا يمكن التراجع."
                confirmText="حذف"
                cancelText="إلغاء"
            />
        </div>
    );
};

export default ModernPackageRequestsScreen;
